import { useCallback } from 'react';
import { setTeams, setTeamLibraryRefresh } from '../store/teamsStore';
import {
  setTeamsModalMode,
  setSelectedTeam,
  setShowTeamsModal,
  setActiveTab,
  setCommandPaletteTeamDetailId,
} from '../store/uiModalStore';
import { useWorkspaceStore, setWorkspaces, setCurrentWorkspace } from '../store/workspaceStore';
import { setCollections } from '../store/collectionsStore';
import { workspaceService } from '../services/workspaceService';
import { collectionService } from '../services/collectionService';
import teamsService from '../services/teamsService';
import { invalidateTeamDetailWorkspacesCache } from '../components/Teams';
import { isBackendWorkspace, mergeRemoteAndLocalWorkspaces } from '../utils/workspaceStorage';
import { listLocalWorkspaceMetasResolved } from '../services/localWorkspaceStore';

/**
 * Team handlers (create/update/modal/palette + join + share-success).
 * handleTeamJoined refreshes workspaces/collections via the stores;
 * handleShareWithTeamSuccess refreshes via reloadWorkspacesFromServer (passed in
 * from useWorkspaces).
 *
 * @param {{ toastT: Function, isAuthenticated: boolean, reloadWorkspacesFromServer: Function }} deps
 */
export function useTeams({ toastT, isAuthenticated, reloadWorkspacesFromServer }) {
  const handleTeamsModal = useCallback((mode, team = null) => {
    setTeamsModalMode(mode);
    setSelectedTeam(team);
    setShowTeamsModal(true);
  }, []);

  const handleTeamCreated = useCallback((newTeam) => {
    setTeams((prev) => [...prev, newTeam]);
    setShowTeamsModal(false);
    toastT('toast.teamCreated', 'success', { name: newTeam.name });
  }, [toastT]);

  const handleTeamUpdated = useCallback((updatedTeam) => {
    setTeams((prev) => prev.map((team) => (team.id === updatedTeam.id ? updatedTeam : team)));
    setShowTeamsModal(false);
    toastT('toast.teamUpdated', 'success', { name: updatedTeam.name });
  }, [toastT]);

  const handleOpenTeamFromCommandPalette = useCallback((team) => {
    if (!isAuthenticated || !team?.id) return;
    setActiveTab('teams');
    setCommandPaletteTeamDetailId(team.id);
  }, [isAuthenticated]);

  const clearCommandPaletteTeamDetail = useCallback(() => {
    setCommandPaletteTeamDetailId(null);
  }, []);

  const handleTeamJoined = useCallback(async (joinedTeam) => {
    setTeams((prev) => [...prev, joinedTeam]);
    setShowTeamsModal(false);
    toastT('toast.teamJoined', 'success', { name: joinedTeam.name });

    // Refresh workspaces and collections to show team-shared content.
    try {
      await new Promise((resolve) => setTimeout(resolve, 500)); // let backend perms propagate
      const userWorkspaces = await workspaceService.getWorkspaces();
      const remoteFlat = Array.isArray(userWorkspaces)
        ? userWorkspaces
        : userWorkspaces?.all_workspaces || [];
      const manifestLocals = await listLocalWorkspaceMetasResolved();
      const merged = mergeRemoteAndLocalWorkspaces(remoteFlat, manifestLocals);
      setWorkspaces(merged);

      const currentWorkspace = useWorkspaceStore.getState().currentWorkspace;
      if (currentWorkspace) {
        const updatedWorkspace = merged.find((ws) => ws.id === currentWorkspace.id);
        if (updatedWorkspace) setCurrentWorkspace(updatedWorkspace);

        const wsForFetch = updatedWorkspace || currentWorkspace;
        if (wsForFetch && isBackendWorkspace(wsForFetch, isAuthenticated)) {
          try {
            await new Promise((resolve) => setTimeout(resolve, 200));
            const workspaceCollections = await collectionService.getCollections(wsForFetch.id);
            setCollections(workspaceCollections);
          } catch (error) {
            console.error('Failed to refresh collections after joining team:', error);
            if (!error.message.includes('403')) {
              toastT('toast.refreshCollectionsFailed', 'warning');
            }
          }
        }
      }
    } catch (error) {
      console.error('Failed to refresh workspaces after joining team:', error);
      toastT('toast.refreshWorkspacesFailed', 'warning');
    }
  }, [toastT, isAuthenticated]);

  const handleShareWithTeamSuccess = useCallback(async (result) => {
    const teamId = result?.teamId;
    if (teamId != null) {
      invalidateTeamDetailWorkspacesCache(teamId);
      setTeamLibraryRefresh({ teamId: Number(teamId), at: Date.now() });
    }
    await reloadWorkspacesFromServer();
    try {
      const userTeams = await teamsService.getTeams();
      const teamsArr = Array.isArray(userTeams) ? userTeams : (userTeams?.data ?? []);
      setTeams(teamsArr);
    } catch (error) {
      console.error('Failed to reload teams after share:', error);
    }
  }, [reloadWorkspacesFromServer]);

  return {
    handleTeamsModal,
    handleTeamCreated,
    handleTeamUpdated,
    handleOpenTeamFromCommandPalette,
    clearCommandPaletteTeamDetail,
    handleTeamJoined,
    handleShareWithTeamSuccess,
  };
}
